// ---------------------------------------------------------------------------
// IMPORTS
// ---------------------------------------------------------------------------

// ------NODE MODULES---------------------------------------------------------
import { useState } from 'react';

import { useLinkClickHandler, useLoaderData, Link } from 'react-router-dom';

import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos';

// ------FILE MODULES---------------------------------------------------------
import { PAGE_ROUTES } from '../../../../config/constants.config';

import { decodeAllJwts, mapPresentationRequests } from '../../../../helpers/jwt.helpers';
import { isOk, get, getResponseJson } from '../../../../helpers/axios.helper';

import CatalogCredentialTable from '../../../../components/private/Tables/CatalogCredentialTable.component';

import PresentationRequestTemplateTable from '../../../../components/private/Tables/PresentationRequestTemplateTable.component';

// ---------------------------------------------------------------------------
// PRIVATE
// ---------------------------------------------------------------------------
const DEFAULT_TRANSLATE = (string) => {
  return string;
};
const DEFAULT_SET_ERROR_POPUP = () => {};
const DEFAULT_IS_FAILED = false;
const DEFAULT_CHILDREN = null;

function PresentationRequestLoadFailed(props) {
  const translate = props.translate || DEFAULT_TRANSLATE;
  const isFailed = props.isFailed || DEFAULT_IS_FAILED;
  const children = props.children || DEFAULT_CHILDREN;

  if (!isFailed) {
    return children;
  }

  return (
    <Grid item>
      <Grid container direction='row' alignItems='center' justifyContent='center'>
        <Grid item>
          <Typography>{translate('presentation-request-detail-page-failed-to-load')}</Typography>
        </Grid>
      </Grid>
    </Grid>
  );
}

async function loadPresentationRequest(presentationRequestId, setIsLoading, setPresentation, setErrorPopup) {
  setIsLoading(true);

  try {
    const prResponse = await get(`/dit/presentationRequest/${presentationRequestId}`);

    if (isOk(prResponse.status)) {
      const presentations = await mapPresentationRequests(decodeAllJwts([getResponseJson(prResponse)]), false);

      if (presentations.length > 0) {
        setPresentation(presentations[0]);
      }
    }
  } catch (exception) {
    setErrorPopup(exception);
  }

  setIsLoading(false);
}

// ---------------------------------------------------------------------------
// EXPORTS
// ---------------------------------------------------------------------------
export async function presentationRequestDetailsLoader({ params }) {
  return params.presentationRequestId;
}

export default function PresentationRequestDetails(props) {
  const translate = props.translator || DEFAULT_TRANSLATE;
  const setErrorPopup = props.setErrorPopup || DEFAULT_SET_ERROR_POPUP;

  const presentationRequestId = useLoaderData();

  const [isLoading, setIsLoading] = useState(undefined);
  const [presentation, setPresentation] = useState(undefined);
  const [selectedCredentials, setSelectedCredentials] = useState(undefined);

  const failedToGetPresentation = presentation === undefined;

  if (isLoading === undefined) {
    loadPresentationRequest(presentationRequestId, setIsLoading, setPresentation, setErrorPopup);
  }

  let credentials = [];
  if (!isLoading && !failedToGetPresentation) {
    const newSelectedCredentials = {};
    credentials = presentation.decoded.pr.data;

    if (selectedCredentials === undefined) {
      presentation.decoded.pr.data.forEach((credential) => {
        newSelectedCredentials[credential.field_name] = credential.levelOfAssurance;
      });
      setSelectedCredentials(newSelectedCredentials);
    }
  }

  return (
    <Grid container direction='column' alignItems='stretch' spacing={5}>
      <Grid item>
        <Grid container direction='row' justifyContent='space-between'>
          <Grid item>
            <Grid container spacing={1}>
              <Grid item>
                <Typography
                  sx={{
                    color: '#1B5255',
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    textDecoration: 'none',
                    'a:visited': {
                      textDecoration: 'none',
                      color: '#1B5255'
                    },
                    'a:link': { textDecoration: 'none' },
                    'a:hover': { textDecoration: 'none' },
                    'a:active': { textDecoration: 'none' }
                  }}>
                  <Link to={PAGE_ROUTES.AdminPresentations}>{translate('presentations-page-title')}</Link>
                </Typography>
              </Grid>
              <Grid item>
                <Typography sx={{ color: '#1B5255', fontSize: '2rem' }}>-</Typography>
              </Grid>
              <Grid item>
                <Typography sx={{ color: '#1B5255', fontSize: '2rem' }}>
                  {translate('presentations-send-page-title')}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Button
              sx={{
                backgroundColor: '#1B5255',
                color: 'white',
                fontSize: '1.125rem',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#1B5255',
                  boxShadow: '4px 4px 4px rgba(0, 0, 0, 0.25)'
                }
              }}
              onClick={useLinkClickHandler(-1)}>
              <Typography variant='h5' paddingRight={5} paddingLeft={5}>
                {translate('presentations-create-back')}
              </Typography>
              <ArrowForwardIos />
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <PresentationRequestLoadFailed translate={translate} isFailed={!isLoading && failedToGetPresentation}>
        <PresentationRequestTemplateTable
          translate={translate}
          setErrorPopup={setErrorPopup}
          isLoading={isLoading}
          paddingBottom={0}
          presentationRequestTemplates={[presentation]}
          hidePagination
          hideRequestDetails
        />

        <CatalogCredentialTable
          translate={translate}
          isLoading={isLoading}
          catalog={credentials}
          hidePagination
          hideActions
          disableCheckboxes
          maxNameLength={50}
          maxUseCaseLength={50}
          selectedCredentialsLoading={selectedCredentials === undefined}
          selectedCredentials={selectedCredentials}
        />
      </PresentationRequestLoadFailed>
    </Grid>
  );
}
